import type { VideoSelection } from "@unbox/api";
import { CurrencyDollarCircle } from "@untitledui/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { Focusable } from "react-aria-components";
import { useAffectedAdCampaigns } from "@/api/assurance/actions";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { useResizeObserver } from "@/hooks/use-resize-observer";
import { cx } from "@/utils/cx";
import { pluralize } from "@/utils/text";

type CampaignKey = { advertiserId: string; campaignId: string };

const keyOf = (c: CampaignKey) => `${c.advertiserId}::${c.campaignId}`;

const CampaignName = ({ name }: { name: string }) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const onResize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setIsTruncated(el.scrollWidth > el.clientWidth);
  }, []);

  useResizeObserver({ ref, onResize });

  return (
    <Tooltip title={name} isDisabled={!isTruncated}>
      <Focusable>
        <p ref={ref} className="block w-full truncate text-sm font-medium text-secondary outline-hidden">
          {name}
        </p>
      </Focusable>
    </Tooltip>
  );
};

type RemoveFromAdCampaignFormFieldsProps = {
  form: any;
  disabled?: boolean;
  selection: VideoSelection;
  videoCount: number;
  selectedCampaignIds: CampaignKey[];
  onSelectionChange: (ids: CampaignKey[]) => void;
};

export const RemoveFromAdCampaignFormFields = ({
  form,
  disabled,
  selection,
  videoCount,
  selectedCampaignIds,
  onSelectionChange,
}: RemoveFromAdCampaignFormFieldsProps) => {
  const { data, isLoading } = useAffectedAdCampaigns(selection);
  const campaigns = data?.campaigns ?? [];

  // Default-select all campaigns when the list loads or changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: onSelectionChange identity is stable from parent
  useEffect(() => {
    if (campaigns.length === 0) {
      if (selectedCampaignIds.length > 0) onSelectionChange([]);
      return;
    }
    const availableKeys = new Set(campaigns.map((c) => keyOf(c)));
    const currentKeys = new Set(selectedCampaignIds.map((c) => keyOf(c)));
    const inSync = currentKeys.size === availableKeys.size && [...availableKeys].every((k) => currentKeys.has(k));
    if (currentKeys.size === 0) {
      onSelectionChange(campaigns.map((c) => ({ advertiserId: c.advertiserId, campaignId: c.campaignId })));
    } else if (!inSync) {
      const filtered = selectedCampaignIds.filter((c) => availableKeys.has(keyOf(c)));
      if (filtered.length !== selectedCampaignIds.length) onSelectionChange(filtered);
    }
  }, [campaigns]);

  const selectedKeys = new Set(selectedCampaignIds.map((c) => keyOf(c)));

  const toggle = (campaign: CampaignKey) => {
    if (disabled) return;
    const k = keyOf(campaign);
    if (selectedKeys.has(k)) {
      onSelectionChange(selectedCampaignIds.filter((c) => keyOf(c) !== k));
    } else {
      onSelectionChange([
        ...selectedCampaignIds,
        { advertiserId: campaign.advertiserId, campaignId: campaign.campaignId },
      ]);
    }
  };

  const allSelected = campaigns.length > 0 && selectedKeys.size === campaigns.length;
  const someSelected = selectedKeys.size > 0 && selectedKeys.size < campaigns.length;

  const toggleAll = () => {
    if (disabled) return;
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(campaigns.map((c) => ({ advertiserId: c.advertiserId, campaignId: c.campaignId })));
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <LoadingIndicator size="sm" label="Loading ad campaigns..." />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="flex items-center justify-center py-8">
          <p className="text-sm text-tertiary">
            {videoCount === 1
              ? "This video is not currently in any active ad campaigns."
              : "These videos are not currently in any active ad campaigns."}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2 w-full overflow-hidden">
          <p className="text-sm text-secondary">
            Select the {pluralize(campaigns.length, "ad campaign", "ad campaigns")} to remove{" "}
            {videoCount === 1 ? "this video" : "these videos"} from:
          </p>
          <div className="h-11 max-h-11 px-4 py-3 bg-secondary border-b border-secondary flex items-center gap-3 w-full rounded-t-lg">
            <Checkbox
              isSelected={allSelected}
              isIndeterminate={someSelected}
              onChange={toggleAll}
              isDisabled={disabled}
            />
            <span className="text-xs font-semibold text-quaternary">Campaign name</span>
          </div>
          <div className="flex flex-col gap-2 max-h-64 overflow-auto">
            {campaigns.map((campaign) => {
              const isSelected = selectedKeys.has(keyOf(campaign));
              return (
                <button
                  key={keyOf(campaign)}
                  type="button"
                  onClick={() => toggle(campaign)}
                  disabled={disabled}
                  className={cx(
                    "p-3 rounded-lg bg-primary flex items-center gap-3 text-left w-full min-w-0",
                    isSelected
                      ? "outline outline-2 outline-offset-[-2px] outline-brand"
                      : "outline outline-1 outline-offset-[-1px] outline-secondary",
                  )}
                >
                  <Checkbox isSelected={isSelected} isDisabled={disabled} />
                  <CurrencyDollarCircle className="w-5 h-5 text-tertiary shrink-0" />
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <CampaignName name={campaign.campaignName} />
                    <p className="text-xs text-tertiary">
                      {campaign.campaignType}
                      {videoCount > 1 && campaign.videoCount > 0 && (
                        <> · {pluralize(campaign.videoCount, "video", "videos")}</>
                      )}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <FormField
        control={form.control}
        name="notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Notes (optional)</FormLabel>
            <FormControl>
              <Textarea rows={2} {...field} disabled={disabled} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
